import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { appendAuditLogEntry } from '../../utils/auditLog';
import { resolveAdminRole } from '../../utils/adminRole';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const defaultTrustees = [
  { id: 'paul-griffiths', name: 'Paul Griffiths', role: 'Trustee', phone: '07882 267686', email: 'paul@wildnights.co.uk', bio: '' },
  { id: 'anne-griffiths', name: 'Anne Griffiths', role: 'Trustee', phone: '07889 046460', email: 'anne@wildnights.co.uk', bio: '' },
  { id: 'barbara-brownley', name: 'Barbara Brownley', role: 'Trustee', phone: '07730 794936', email: 'barbarabrownley@virginmedia.com', bio: '' },
  { id: 'john-vardy', name: 'John Vardy', role: 'Trustee', phone: '07496 912219', email: 'vardyj84@hotmail.co.uk', bio: '' },
  { id: 'ian-woodall', name: 'Ian Woodall', role: 'Trustee', phone: '07474 222232', email: 'iwoodall@hotmail.co.uk', bio: '' },
  { id: 'richard-clarey', name: 'Richard Clarey', role: 'Trustee', phone: '07850 051986', email: 'rcsrichard@yahoo.co.uk', bio: '' },
];

const TrusteeManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [trustees, setTrustees] = useState([]);
  const [trusteeForm, setTrusteeForm] = useState({ name: '', role: 'Trustee', phone: '', email: '', bio: '' });
  const [editingTrusteeId, setEditingTrusteeId] = useState(null);

  // Backend submission state — separate from the localStorage-driven UI above
  const [trusteeSubmitting, setTrusteeSubmitting] = useState(false);
  const [trusteeAdminError, setTrusteeAdminError] = useState('');
  const [trusteeAdminMessage, setTrusteeAdminMessage] = useState('');
  const [deletingTrusteeId, setDeletingTrusteeId] = useState(null);
  const [deleteConfirmTrusteeId, setDeleteConfirmTrusteeId] = useState(null);

  useEffect(() => {
    const storedTrustees = window.localStorage.getItem('clipstone_trustees');
    if (storedTrustees) {
      try {
        const parsedTrustees = JSON.parse(storedTrustees);
        const mergedTrustees = [...parsedTrustees];
        defaultTrustees.forEach((defaultTrustee) => {
          if (!parsedTrustees.some((trustee) => trustee.id === defaultTrustee.id)) {
            mergedTrustees.push(defaultTrustee);
          }
        });
        setTrustees(mergedTrustees);
        window.localStorage.setItem('clipstone_trustees', JSON.stringify(mergedTrustees));
      } catch (err) {
        console.error('Unable to load trustees:', err);
        setTrustees(defaultTrustees);
        window.localStorage.setItem('clipstone_trustees', JSON.stringify(defaultTrustees));
      }
    } else {
      setTrustees(defaultTrustees);
      window.localStorage.setItem('clipstone_trustees', JSON.stringify(defaultTrustees));
    }

    const checkAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate('/login');
          return;
        }

        const { data: isAdmin, error } = await supabase.rpc('check_is_admin', {
          p_email: session.user.email,
        });

        if (error || !isAdmin) {
          navigate('/account');
          return;
        }

        await resolveAdminRole(session.user.email);

        setAdminInfo({
          email: session.user.email,
          lastSignIn: session.user.last_sign_in_at,
          createdAt: session.user.created_at,
        });
      } catch (err) {
        console.error('Admin access check error:', err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [navigate]);

  // Adds a trustee to tbl_admin via the backend (grants admin permissions).
  // Requires the caller's Supabase session token, same as fetchEvents/AdminEvents
  // pattern — verify_admin_token on the backend checks this email is already
  // in tbl_admin before allowing the insert.
  const addTrusteeToAdminTable = async (name, email) => {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('Your session has expired. Please log in again.');
    }

    const response = await fetch(`${BACKEND_URL}/admin/trustees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ name, email }),
    });

    if (!response.ok) {
      const errBody = await response.json().catch(() => ({}));
      throw new Error(errBody.detail || `Request failed (${response.status})`);
    }

    return response.json();
  };

  const handleTrusteeSubmit = async (event) => {
    event.preventDefault();
    if (!trusteeForm.name.trim()) return;

    setTrusteeAdminError('');
    setTrusteeAdminMessage('');

    if (editingTrusteeId) {
      // Update existing trustee (public-page display only — does not touch tbl_admin)
      const updatedTrustees = trustees.map((trustee) =>
        trustee.id === editingTrusteeId
          ? {
              id: editingTrusteeId,
              name: trusteeForm.name.trim(),
              role: trusteeForm.role || 'Trustee',
              phone: trusteeForm.phone.trim(),
              email: trusteeForm.email.trim(),
              bio: trusteeForm.bio.trim(),
            }
          : trustee
      );
      setTrustees(updatedTrustees);
      window.localStorage.setItem('clipstone_trustees', JSON.stringify(updatedTrustees));
      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Updated',
        entityType: 'trustee',
        entityName: trusteeForm.name.trim() || 'Trustee',
        details: 'Updated trustee details in management page.',
      });
      setTrusteeForm({ name: '', role: 'Trustee', phone: '', email: '', bio: '' });
      setEditingTrusteeId(null);
      return;
    }

    // Creating a new trustee: requires an email, since that's what goes into tbl_admin
    if (!trusteeForm.email.trim()) {
      setTrusteeAdminError('Email is required to add a trustee to tbl_admin.');
      return;
    }

    setTrusteeSubmitting(true);
    try {
      await addTrusteeToAdminTable(trusteeForm.name.trim(), trusteeForm.email.trim());

      const newTrustee = {
        id: Date.now().toString(),
        name: trusteeForm.name.trim(),
        role: trusteeForm.role || 'Trustee',
        phone: trusteeForm.phone.trim(),
        email: trusteeForm.email.trim(),
        bio: trusteeForm.bio.trim(),
      };
      const nextTrustees = [newTrustee, ...trustees];
      setTrustees(nextTrustees);
      window.localStorage.setItem('clipstone_trustees', JSON.stringify(nextTrustees));
      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Added',
        entityType: 'trustee',
        entityName: newTrustee.name,
        details: 'Added trustee to admin access list and trustee directory.',
      });

      setTrusteeForm({ name: '', role: 'Trustee', phone: '', email: '', bio: '' });
      setTrusteeAdminMessage(`${newTrustee.name} was added to tbl_admin and the trustee list.`);
    } catch (err) {
      console.error('Failed to add trustee to tbl_admin:', err);
      setTrusteeAdminError(err.message || 'Could not add trustee to tbl_admin.');
    } finally {
      setTrusteeSubmitting(false);
    }
  };

  const handleEditTrustee = (trustee) => {
    setDeleteConfirmTrusteeId(null);
    setEditingTrusteeId(trustee.id);
    setTrusteeForm({
      name: trustee.name,
      role: trustee.role || 'Trustee',
      phone: trustee.phone || '',
      email: trustee.email || '',
      bio: trustee.bio || '',
    });
    setTrusteeAdminError('');
    setTrusteeAdminMessage('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Removes a trustee from tbl_admin via the backend, keyed by email since
  // that's the one value guaranteed to match between the local trustee list
  // and the real database row (local trustee IDs don't correspond to
  // tbl_admin's integer IDs).
  const handleDeleteTrustee = async (trustee) => {
    if (!trustee.email) {
      setTrusteeAdminError('This trustee has no email on file, so it cannot be removed from tbl_admin.');
      return;
    }

    setTrusteeAdminError('');
    setTrusteeAdminMessage('');
    setDeletingTrusteeId(trustee.id);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Your session has expired. Please log in again.');
      }

      const response = await fetch(
        `${BACKEND_URL}/admin/trustees/${encodeURIComponent(trustee.email.trim().toLowerCase())}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `Request failed (${response.status})`);
      }

      const updatedTrustees = trustees.filter((item) => item.id !== trustee.id);
      setTrustees(updatedTrustees);
      window.localStorage.setItem('clipstone_trustees', JSON.stringify(updatedTrustees));
      appendAuditLogEntry({
        actorEmail: adminInfo?.email,
        action: 'Deleted',
        entityType: 'trustee',
        entityName: trustee.name,
        details: 'Removed trustee from admin access list and trustee directory.',
      });
      setTrusteeAdminMessage(`${trustee.name} was removed from tbl_admin.`);
      setDeleteConfirmTrusteeId(null);
    } catch (err) {
      console.error('Failed to delete trustee from tbl_admin:', err);
      setTrusteeAdminError(err.message || 'Could not remove trustee from tbl_admin.');
    } finally {
      setDeletingTrusteeId(null);
    }
  };

  const handleCancelEdit = () => {
    setDeleteConfirmTrusteeId(null);
    setEditingTrusteeId(null);
    setTrusteeForm({ name: '', role: 'Trustee', phone: '', email: '', bio: '' });
    setTrusteeAdminError('');
    setTrusteeAdminMessage('');
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logoRow}>
            <img src={logo} alt="Logo" style={styles.logo} />
          </div>
          <div style={styles.centreBox}>
            <div style={styles.spinner} />
            <p style={styles.mutedText}>Loading trustee management...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logo} />
        </div>

        <div style={styles.header}>
          <div style={styles.adminBadge}>Admin</div>
          <h2 style={styles.title}>Trustee Management</h2>
          <p style={styles.subtitle}>Manage trustees</p>
        </div>

        <div style={styles.infoGrid}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Signed in as</span>
            <span style={styles.infoValue}>{adminInfo?.email}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Last sign in</span>
            <span style={styles.infoValue}>{formatDate(adminInfo?.lastSignIn)}</span>
          </div>
        </div>

        <div style={styles.sectionCard}>
          <div style={styles.sectionHeading}>Trustee management</div>
          <p style={styles.sectionText}>Add trustees for the public Meet Our Trustees page.</p>

          <div style={styles.sectionGrid}>
            <form onSubmit={handleTrusteeSubmit} style={styles.formCard}>
              <h3 style={styles.cardTitle}>{editingTrusteeId ? 'Edit trustee' : 'Add a new trustee'}</h3>

              {trusteeAdminError && (
                <div style={styles.errorBanner}>{trusteeAdminError}</div>
              )}
              {trusteeAdminMessage && (
                <div style={styles.successBanner}>{trusteeAdminMessage}</div>
              )}

              <input
                style={styles.input}
                value={trusteeForm.name}
                onChange={(event) => setTrusteeForm({ ...trusteeForm, name: event.target.value })}
                placeholder="Full name"
              />
              <input
                style={styles.input}
                value={trusteeForm.role}
                onChange={(event) => setTrusteeForm({ ...trusteeForm, role: event.target.value })}
                placeholder="Role"
              />
              <input
                style={styles.input}
                value={trusteeForm.phone}
                onChange={(event) => setTrusteeForm({ ...trusteeForm, phone: event.target.value })}
                placeholder="Phone"
              />
              <input
                style={styles.input}
                value={trusteeForm.email}
                onChange={(event) => setTrusteeForm({ ...trusteeForm, email: event.target.value })}
                placeholder="Email"
              />
              <textarea
                style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                value={trusteeForm.bio}
                onChange={(event) => setTrusteeForm({ ...trusteeForm, bio: event.target.value })}
                placeholder="Short note or profile summary"
              />
              <div style={styles.formBtnRow}>
                <button
                  type="submit"
                  disabled={trusteeSubmitting}
                  style={trusteeSubmitting ? { ...styles.primaryBtn, opacity: 0.6, cursor: 'not-allowed' } : styles.primaryBtn}
                >
                  {trusteeSubmitting ? 'Saving...' : editingTrusteeId ? 'Update trustee' : 'Save trustee'}
                </button>
                {editingTrusteeId && <button type="button" onClick={handleCancelEdit} style={styles.secondaryBtn}>Cancel</button>}
              </div>
            </form>
          </div>

          <div style={styles.listSection}>
            <div style={styles.listBlock}>
              <h3 style={styles.cardTitle}>Current trustees</h3>
              {trustees.length === 0 ? (
                <p style={styles.mutedText}>No trustees added yet.</p>
              ) : (
                trustees.map((trustee) => (
                  <div key={trustee.id} style={styles.listItem}>
                    <div>
                      <div style={styles.listItemTitle}>{trustee.name}</div>
                      <div style={styles.listItemMeta}>{trustee.role || 'Trustee'}{trustee.email ? ` • ${trustee.email}` : ''}</div>
                      {trustee.phone && <div style={styles.listItemMeta}>Phone: {trustee.phone}</div>}
                    </div>
                    <div style={styles.listItemActions}>
                      <button type="button" style={styles.actionButton} onClick={() => handleEditTrustee(trustee)}>Edit</button>
                      {deleteConfirmTrusteeId === trustee.id ? (
                        <div style={styles.deleteConfirmWrap}>
                          <span style={styles.deleteConfirmText}>Delete?</span>
                          <button
                            type="button"
                            style={deletingTrusteeId === trustee.id ? { ...styles.confirmDeleteButton, opacity: 0.6, cursor: 'not-allowed' } : styles.confirmDeleteButton}
                            disabled={deletingTrusteeId === trustee.id}
                            onClick={() => handleDeleteTrustee(trustee)}
                          >
                            {deletingTrusteeId === trustee.id ? 'Deleting...' : 'Yes'}
                          </button>
                          <button
                            type="button"
                            style={styles.cancelDeleteButton}
                            onClick={() => setDeleteConfirmTrusteeId(null)}
                            disabled={deletingTrusteeId === trustee.id}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          style={styles.deleteButton}
                          onClick={() => setDeleteConfirmTrusteeId(trustee.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

          </div>
        </div>

        <div style={styles.actions}>
          <button style={styles.secondaryBtn} onClick={() => navigate('/admin-account')}>
            <i className="icon-left" style={styles.actionIcon} />
            Back to admin dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 20px)',
    background: 'linear-gradient(135deg, #0f1f18 0%, #1e3f32 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#1a2e25',
    border: '1px solid #2d4f3c',
    borderRadius: '16px',
    padding: 'clamp(18px, 4vw, 40px)',
    width: '100%',
    maxWidth: '960px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logo: {
    height: 'clamp(60px, 12vw, 80px)',
    width: 'auto',
  },
  centreBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '32px 0',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #2d4f3c',
    borderTop: '4px solid #4ade80',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  mutedText: {
    color: '#6b9e82',
    fontSize: '14px',
    margin: 0,
  },
  header: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  adminBadge: {
    display: 'inline-block',
    background: '#4ade80',
    color: '#0f1f18',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    padding: '4px 12px',
    borderRadius: '20px',
    marginBottom: '10px',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  subtitle: {
    color: '#6b9e82',
    fontSize: '14px',
    margin: 0,
  },
  infoGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '28px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    background: '#152318',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '12px 16px',
  },
  infoLabel: {
    color: '#6b9e82',
    fontSize: '13px',
    fontWeight: '500',
  },
  infoValue: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'right',
    maxWidth: '100%',
    wordBreak: 'break-word',
  },
  sectionCard: {
    background: '#152318',
    border: '1px solid #2d4f3c',
    borderRadius: '12px',
    padding: '18px',
    marginBottom: '20px',
  },
  sectionHeading: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '700',
    marginBottom: '6px',
  },
  sectionText: {
    color: '#6b9e82',
    fontSize: '13px',
    margin: '0 0 14px 0',
    lineHeight: 1.5,
  },
  sectionGrid: {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  },
  formCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    background: '#0f1f18',
    borderRadius: '10px',
    padding: '12px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  errorBanner: {
    background: '#3a1a1a',
    color: '#f87171',
    border: '1px solid #7f1d1d',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '12px',
  },
  successBanner: {
    background: '#0f2a1a',
    color: '#4ade80',
    border: '1px solid #14532d',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '12px',
  },
  input: {
    width: '100%',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '10px 12px',
    background: '#1a2e25',
    color: '#ffffff',
    fontSize: '13px',
    boxSizing: 'border-box',
  },
  formBtnRow: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  listSection: {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginTop: '12px',
  },
  listBlock: {
    background: '#0f1f18',
    borderRadius: '10px',
    padding: '12px',
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    padding: '10px 0',
    borderBottom: '1px solid #22392c',
  },
  listItemTitle: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
  },
  listItemMeta: {
    color: '#6b9e82',
    fontSize: '12px',
    marginTop: '2px',
  },
  listItemActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  actionButton: {
    padding: '8px 12px',
    background: '#2d4f3c',
    color: '#ffffff',
    border: '1px solid #4ade80',
    borderRadius: '8px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  deleteButton: {
    padding: '8px 12px',
    background: '#991b1b',
    color: '#ffffff',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  deleteConfirmWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: '#3a1a1a',
    border: '1px solid #7f1d1d',
    borderRadius: '8px',
    padding: '6px 8px',
  },
  deleteConfirmText: {
    color: '#fecaca',
    fontSize: '12px',
    fontWeight: '600',
  },
  confirmDeleteButton: {
    border: 'none',
    background: '#dc2626',
    color: '#fff',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  cancelDeleteButton: {
    border: '1px solid #475569',
    background: 'transparent',
    color: '#cbd5e1',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  statusPill: {
    background: '#14532d',
    color: '#4ade80',
    borderRadius: '999px',
    padding: '4px 8px',
    fontSize: '11px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
  },
  primaryBtn: {
    padding: '12px 14px',
    background: '#4ade80',
    color: '#0f1f18',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '12px 14px',
    background: 'transparent',
    color: '#6b9e82',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  actionIcon: {
    display: 'inline-block',
    fontSize: '1.05em',
    marginRight: '8px',
    verticalAlign: 'middle',
  },
};

const styleTag = document.createElement('style');
styleTag.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleTag);

export default TrusteeManagement;